<script>
    import {Report, Topic, Section, Toolbar, Notebook, PersistedLinkButton} from '@manaakiwhenua/frag-svelte'
    import TopicMetadata from './metadata/TopicMetadata.svelte';
    import PrintOnly from './PrintOnly.svelte';
    import TopicPicker from './TopicPicker.svelte';
    import EditableTitle from './EditableTitle.svelte';
    import SaveAndEdit from './SaveAndEdit.svelte';
    import OurEnvironmentFooter from './OurEnvironmentFooter.svelte';
    import {ReportAPI} from '../api/report';
    import {createEventDispatcher} from 'svelte';
    import {setActiveView, viewsForSections} from './topic-state'
    import PrintedMetadata from './metadata/PrintedMetadata.svelte'
    import NotebookWrapper from './NotebookWrapper.svelte';
    import {getContext, setContext, onDestroy} from 'svelte';
    import {writable} from 'svelte/store';
    import AsyncPrintButton from "./AsyncPrintButton.svelte";

    export let report;
    export let reportLink = '';
    export let header;
    export let categories;
    export let topicState = report && report.topics || {};
    export let iconClass;
    export let reportKind;
    export let extraClass = '';
    export let editing = false;
    export let editable = true;

    let topicStateMemo = topicState;

    const printing = getContext('printStore') || writable(false);
    setContext('printStore', printing);

    const reportSavedStore = writable(0);
    setContext('reportSaved', reportSavedStore);

    let titleMemo = document.title;
    $: {
        // when the report is being printed... set the doc title to the report title, so the printed reports
        // use the title of the report, not the page they're embedded in
        if ($printing) {
            titleMemo = document.title;
            document.title = report && report.title || '';
        } else {
            document.title = titleMemo;
        }
    }

    onDestroy(function () {
        printing.set(false);
        document.title = titleMemo;
    });

    let reportComponent;

    const dispatch = createEventDispatcher();

    $: shared = report && report.shared;

    $: linkIfShared = shared && reportLink;

    let rootElement;

    function closeReport() {
        if (rootElement) {
            const event = new CustomEvent('closeReport', {bubbles: true});
            rootElement.dispatchEvent(event);
        }
    }

    const reportAPI = new ReportAPI();

    let topicPickerVisible = false;

    function showTopicPicker(e) {
        console.log(e);
        topicPickerVisible = true;
    }

    function closeTopicPicker() {
        topicPickerVisible = false;
    }

    function isIncluded(topic) {
        return topicState[topic.id];
    }

    $: includedCategories = categories
        .filter(category => category.topics.some(t => topicState[t.id]));

    $: includedTopics = includedCategories.reduce((ts, cat) => [...ts, ...cat.topics], []).filter(t => topicState[t.id]);

    let dirty;

    function updateTopicState(event) {
        console.log(event);
        if (!topicStateMemo) topicStateMemo = topicState;
        topicState = event.detail.newState;
        dirty = editable;
    }

    let activeCategory;



    function toggleEditing() {
        editing = !editing;
    }

    function onTitleChange(e) {
        dirty = editable;
    }

    function cancelEdit() {
        editing = false;
        reportTitle = report.title;
        if (topicStateMemo) {
            dispatch('reportUpdated', {
                topics: topicStateMemo
            })
        }
        dirty = false;
    }

    let reportTitle = report && report.title || '';

    function saveChanges() {
        reportAPI.saveReport({
            topics: topicState,
            title: reportTitle,
            id: report.id
        }).then((result) => {
            const {topics, title} = result.updateReportById.report;
            dispatch('reportUpdated', {topics, title});
            topicStateMemo = topics;
            editing = false;
            dirty = false;
            reportSavedStore.update(val => val + 1);
        });
    }

    function createShareLink() {
        reportAPI.updateReportShared({id: report.id}, true)
            .then((result) => {
                const shared = result.updateReportById.report.shared;
                dispatch('reportUpdated', {shared});
            });
    }

    function unshare() {
        reportAPI.updateReportShared({id: report.id}, false)
            .then((result) => {
                const shared = result.updateReportById.report.shared;
                dispatch('reportUpdated', {shared});
            })
    }

    function changeActiveView(e, topic, section) {
        console.log(e, topic, section, reportComponent);
        console.log(topicState);

        topicState = setActiveView(topicState, topic, section, e.detail.view);
        dirty = editable;
        if (editable) editing = true;
    }

    $: views = viewsForSections(topicState);

    function viewForSection(views, section) {
        const vid = views.get(section.uuid);
        const idx = vid ? section.notebookViews.findIndex(v => v.id === vid) : 0;
        return idx !== -1 ? idx : 0;
    }

    function theTime() {
        return (new Date()).toLocaleTimeString('en-NZ');
    }

    function theDate() {
        return (new Date()).toLocaleDateString('en-NZ');
    }

    let persistedLinkVisible;
    let asyncPrintVisible;

    function asyncPrintToggled(e) {
        if (e.detail && persistedLinkVisible) {
            persistedLinkVisible = false;
        }
    }

    function persistedLinkToggled(e) {
        if (e.detail && asyncPrintVisible) {
            asyncPrintVisible = false;
        }
    }

    function hideDropdowns(e) {
        persistedLinkVisible = false;
        asyncPrintVisible = false;
    }
</script>

<style>
    .topic-picker-container {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;

        height: 100%;
        width: 100%;

        background: black;

        z-index: 999999;

        display: flex;
        align-items: center;
        justify-content: center;

        max-height: 100vh;
        overflow-y: auto;
    }

    .topic-picker-content {
        background: white;
        padding: 2em 2em 0.5em;
    }

    .close-topic-picker {
        text-align: right;
    }

    @keyframes spinner {
        to {transform: rotate(360deg);}
    }

    @media print {
        :global(.lower-controls, .sort-btn) {
            display: none;
        }
    }

    :global(@page) {
        margin: 3em 1em;
    }

    :global(.polygon-header .frag-notebook) {
        display: flex;
    }

    :global(.polygon-header .frag-notebook [data-cell]:first-child) {
        flex: 1;
    }

    :global(.frag-notebook) {
        min-height: 40px;
    }

    :global(.frag-notebook .observablehq--running:before), :global(.loading-spinner) {
        content: '';
        position: absolute;
        height: 100%;
        width: 100%;
        background: white;
        z-index: 10000;
    }

    :global(.frag-notebook .observablehq--running:after), :global(.loading-spinner) {
        content: '';
        z-index: 10001;
        box-sizing: border-box;
        position: absolute;
        top: 50%;
        left: 50%;
        width: 20px;
        height: 20px;
        margin-top: -10px;
        margin-left: -10px;
        border-radius: 50%;
        border: 2px solid #ccc;
        /*border-top-color: rgb(204, 204, 204);*/
        border-top-color: #333;
        -webkit-animation: .6s linear infinite spinner;
        animation: .6s linear infinite spinner;
    }

    .report-title {
        padding: 1em;
    }

    .report-kind {
        margin-bottom: 8px;
    }

    @media print {
        .report-kind {
            margin-top: 0;
        }

        .report-title {
            padding: 0;
        }

        :global(.category-title) {
            font-size: 1em;
            margin-bottom: 5px;
            font-weight: bold;
            padding: 0;
        }
    }

    :global(span[slot=extraButtons] button) {
        padding: 0 0.5em;
        margin: 0 0.1em;
        height: 3em;
        vertical-align: top;
    }

    :global(.topic-header ~ [slot=section]) {
        page-break-before: avoid;
        break-before: avoid;
    }

    [slot=closeButton] {
        position: absolute;
        top: 1em;
        right: 1em;
        border: none;
        background: transparent;
        margin: 0;
        padding: 0;
    }

    [slot=closeButton] > i.icon {
        position: static;
    }

    @media print {

    }
</style>

<div bind:this={rootElement} class={extraClass} on:click={hideDropdowns}>
    <Report bind:this={reportComponent}
            categories={includedCategories}
            {header}
            isTopicPickerEnabled={editing}
            alwaysShowCategoryTabs={true}
            on:showTopicPicker={showTopicPicker}
            bind:activeCategory={activeCategory}>
        <div slot="toolbar">
            <Toolbar printEnabled={false}>
                <span slot="extraButtons" >
                    <SaveAndEdit {editable}
                                 bind:editing={editing}
                                 dirty={dirty}
                                 on:cancel={cancelEdit}
                                 on:save={saveChanges} />
                    <PersistedLinkButton
                            link={linkIfShared}
                            {createShareLink}
                            {unshare}
                            on:change={persistedLinkToggled}
                            bind:dropdownVisible={persistedLinkVisible} />
                    <AsyncPrintButton
                            reportId={report.id}
                            {reportTitle}
                            on:change={asyncPrintToggled}
                            disabled={dirty}
                            bind:dropdownVisible={asyncPrintVisible}
                            />
                </span>
                <button slot="closeButton"
                        class="div-like"
                        on:click={closeReport}>
                    <i class="close-button icon icon-close_large"></i>
                </button>
            </Toolbar>
        </div>

        <div slot="header" let:header={theHeader}>
            <div class="report-title">
                <h3 class="report-kind"><i class={iconClass}></i> { reportKind }</h3>
                {#if report}
                    <EditableTitle bind:title={reportTitle} {editing} on:change={onTitleChange} />
                {/if}

                <PrintOnly let:printing>
                    {#if printing}
                        <p class="print-only report-time-stamp">
                            Report prepared by Our Environment, {theTime()} {theDate()} Manaaki Whenua - Landcare
                            Research
                        </p>
                    {/if}
                </PrintOnly>
            </div>
            {#if theHeader}
                <Topic {...theHeader} showHeader={false} uuid={'area-report-header'}>
                    <div slot="header">

                    </div>
                    <div slot="section" let:section>
                        <Section {...section} showTitle={false} let:activeView={headerView}>
                            <Notebook extraClass={headerView.extraClass}
                                      notebook={headerView.notebook}
                                      cells={headerView.cells}
                                      overrides={headerView.overrides} />
                        </Section>
                    </div>
                </Topic>
            {/if}
        </div>

        <div slot="topic" let:topic let:category>
            {#if topicState[topic.id]}
                <Topic sections={topic.sections} title={topic.title} showHeader={true} hasInfo={true} uuid={topic.id}>
                    <div slot="section" let:section>
                        <Section {...section}
                                 activeViewIdx={viewForSection(views, section)}
                                 showTitle={section.title != topic.title}
                                 on:changeActiveView={(e) => changeActiveView(e, topic, section)}  let:activeView={view}>
                            <NotebookWrapper retry={view.retry}>
                                {#if view.notebook}
                                    <Notebook notebook={view.notebook} cells={view.cells} overrides={view.overrides} extraClass={view.extraClass} />
                                {:else if view.component}
                                    <svelte:component this={view.component} {...view.props || {}} />
                                {:else}
                                    {@debug view}
                                {/if}
                            </NotebookWrapper>
                            {#if topic.sections.length > 1 && topic.sections.indexOf(section) < topic.sections.length - 1}
                                <hr/>
                            {/if}
                        </Section>
                    </div>

                    <div slot="info" let:showInfo class="hidden-print">
                        {#if showInfo}
                            <TopicMetadata topicId={topic.id} />
                        {/if}
                    </div>
                </Topic>
            {/if}
        </div>

        <div slot="footer" let:printing>
            <PrintedMetadata {includedTopics} />

            <OurEnvironmentFooter />
        </div>
    </Report>
</div>

{#if topicPickerVisible}
    <div class="topic-picker-container">
        <div class="topic-picker-content">
            <TopicPicker {categories} state={topicState} on:toggleTopic={updateTopicState} on:close={closeTopicPicker} {activeCategory} />
        </div>
    </div>
{/if}

